/**
 * EONEL Interactive Product Explorer
 * Vanilla JS. One instance per section. No external libraries.
 *
 * Architecture:
 * - Single class, scoped to one section root element (supports multiple
 *   instances on a page and Theme Editor re-render without collisions).
 * - One state object drives both the arc transform math and the detail
 *   card content — single source of truth, no framework/state library.
 * - All animated properties are transform/opacity only (GPU-compositable).
 * - Pointer Events API unifies touch + mouse (no separate listeners).
 */

(function () {
  'use strict';

  var VISIBLE_RANGE = 4;      // items beyond this offset are hidden (not rendered)
  var MIN_SCALE = 0.55;
  var SCALE_STEP = 0.14;
  var OPACITY_STEP = 0.26;
  var MIN_OPACITY = 0.15;
  var ANGLE_STEP_DEG = 15;    // angular spacing between arc items
  var SETTLE_EASE = 0.22;     // per-frame lerp factor for spring settle
  var SETTLE_EPSILON = 0.003;
  var PROJECTION_MS = 140;    // how far momentum projects index after release
  var DRAG_CLICK_THRESHOLD = 6; // px moved before a tap is treated as a drag

  function clamp(val, min, max) {
    return Math.max(min, Math.min(max, val));
  }

  function EonelExplorer(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.destroyed = false;
    this._onPointerDown = this.onPointerDown.bind(this);
    this._onPointerMove = this.onPointerMove.bind(this);
    this._onPointerUp = this.onPointerUp.bind(this);
    this._onResize = this.debounce(this.onResize.bind(this), 150);
  }

  EonelExplorer.prototype.init = function () {
    var dataScript = this.root.querySelector('[data-eonel-product-data]');
    if (!dataScript) return;

    try {
      this.data = JSON.parse(dataScript.textContent);
    } catch (e) {
      this.data = [];
    }
    if (!this.data.length) return;

    this.arcEl = this.root.querySelector('[data-eonel-arc]');
    this.trackEl = this.root.querySelector('[data-eonel-arc-track]');
    this.hintEl = this.root.querySelector('[data-eonel-swipe-hint]');
    this.items = Array.prototype.slice.call(this.root.querySelectorAll('[data-eonel-arc-item]'));
    this.dots = Array.prototype.slice.call(this.root.querySelectorAll('[data-eonel-dot]'));

    this.detail = {
      image: this.root.querySelector('[data-eonel-detail-image]'),
      badge: this.root.querySelector('[data-eonel-detail-badge]'),
      title: this.root.querySelector('[data-eonel-detail-title]'),
      desc: this.root.querySelector('[data-eonel-detail-desc]'),
      price: this.root.querySelector('[data-eonel-detail-price]'),
      compare: this.root.querySelector('[data-eonel-detail-compare]'),
      discount: this.root.querySelector('[data-eonel-detail-discount]'),
      cta: this.root.querySelector('[data-eonel-detail-cta]'),
      ctaText: this.root.querySelector('[data-eonel-cta-text]')
    };

    this.itemCount = this.items.length;
    this.arcEnabled = !this.root.classList.contains('eonel-explorer--flat');
    this.autoplay = this.root.getAttribute('data-autoplay') === 'true';
    this.autoplaySpeed = parseInt(this.root.getAttribute('data-autoplay-speed'), 10) || 3500;

    this.state = {
      floatIndex: 0,
      activeIndex: 0,
      dragging: false,
      pointerId: null,
      startX: 0,
      startFloatIndex: 0,
      lastX: 0,
      lastT: 0,
      velocity: 0,
      moved: 0
    };

    this.rafId = null;
    this.renderScheduled = false;
    this.autoplayTimer = null;

    this.measure();
    this.bindEvents();
    this.renderArc(0);
    this.commit(0, true);

    // Preload the first image eagerly (already loading="eager" in markup),
    // warm neighbors so the first swipe feels instant.
    this.preloadNeighbors(0);

    this.root.eonelExplorerInstance = this;
    this.startAutoplay();
  };

  /* -------------------- measurement -------------------- */

  EonelExplorer.prototype.measure = function () {
    var styles = getComputedStyle(this.root);
    var cardW = parseFloat(styles.getPropertyValue('--eonel-arc-card-w')) || 100;
    var spacing = parseFloat(styles.getPropertyValue('--eonel-card-spacing')) || 14;
    this.spacingPx = cardW * 0.62 + spacing + 40; // effective horizontal step between items

    var rect = this.arcEl.getBoundingClientRect();
    this.radiusX = rect.width * 0.46;
    this.radiusY = rect.height * 0.62;
  };

  EonelExplorer.prototype.onResize = function () {
    if (this.destroyed) return;
    this.measure();
    this.renderArc(this.state.floatIndex);
  };

  /* -------------------- event binding -------------------- */

  EonelExplorer.prototype.bindEvents = function () {
    this.trackEl.addEventListener('pointerdown', this._onPointerDown);
    window.addEventListener('pointermove', this._onPointerMove);
    window.addEventListener('pointerup', this._onPointerUp);
    window.addEventListener('pointercancel', this._onPointerUp);
    window.addEventListener('resize', this._onResize);

    this.items.forEach(
      function (item) {
        item.addEventListener('click', this.onItemClick.bind(this, item));
      }.bind(this)
    );

    this.dots.forEach(
      function (dot) {
        dot.addEventListener('click', this.onDotClick.bind(this, dot));
      }.bind(this)
    );

    // Theme Editor hooks — scoped to this section instance only.
    this._onBlockSelect = this.onBlockSelect.bind(this);
    this._onSectionUnload = this.onSectionUnload.bind(this);
    document.addEventListener('shopify:block:select', this._onBlockSelect);
    document.addEventListener('shopify:section:unload', this._onSectionUnload);
  };

  EonelExplorer.prototype.onBlockSelect = function (e) {
    if (!e.detail || e.detail.sectionId !== this.sectionId) return;
    var item = this.items.filter(function (el) {
      return el.getAttribute('data-block-id') === e.detail.blockId;
    })[0];
    if (item) {
      this.stopAutoplay();
      this.animateToIndex(parseInt(item.getAttribute('data-index'), 10));
    }
  };

  EonelExplorer.prototype.onSectionUnload = function (e) {
    if (e.detail && e.detail.sectionId === this.sectionId) {
      this.destroy();
    }
  };

  /* -------------------- pointer / drag -------------------- */

  EonelExplorer.prototype.onPointerDown = function (e) {
    if (e.button !== undefined && e.button !== 0) return;
    this.stopAutoplay();
    this.cancelSettle();
    this.hideHint();

    var s = this.state;
    s.dragging = true;
    s.pointerId = e.pointerId;
    s.startX = e.clientX;
    s.startFloatIndex = s.floatIndex;
    s.lastX = e.clientX;
    s.lastT = performance.now();
    s.velocity = 0;
    s.moved = 0;

    this.trackEl.setPointerCapture && this.trackEl.setPointerCapture(e.pointerId);
    this.items.forEach(function (item) {
      item.style.willChange = 'transform';
    });
  };

  EonelExplorer.prototype.onPointerMove = function (e) {
    var s = this.state;
    if (!s.dragging || e.pointerId !== s.pointerId) return;

    var now = performance.now();
    var dt = now - s.lastT || 16;
    var dx = e.clientX - s.lastX;
    var instantVelocity = dx / dt;
    s.velocity = s.velocity * 0.75 + instantVelocity * 0.25; // smoothed
    s.lastX = e.clientX;
    s.lastT = now;
    s.moved += Math.abs(dx);

    var totalDelta = e.clientX - s.startX;
    var deltaIndex = -totalDelta / this.spacingPx;
    s.floatIndex = clamp(s.startFloatIndex + deltaIndex, 0, this.itemCount - 1);

    this.scheduleRender();

    var nearest = Math.round(s.floatIndex);
    if (nearest !== s.activeIndex) {
      this.commit(nearest);
    }
  };

  EonelExplorer.prototype.onPointerUp = function (e) {
    var s = this.state;
    if (!s.dragging || (s.pointerId !== null && e.pointerId !== s.pointerId)) return;
    s.dragging = false;

    // index-units-per-ms, projected forward for a natural momentum feel
    var indexVelocity = -s.velocity / this.spacingPx;
    var projectedShift = indexVelocity * PROJECTION_MS;
    var target = clamp(Math.round(s.floatIndex + projectedShift), 0, this.itemCount - 1);

    this.animateToIndex(target);
    this.startAutoplay();
  };

  EonelExplorer.prototype.onItemClick = function (item) {
    if (this.state.moved > DRAG_CLICK_THRESHOLD) return; // was a drag, not a tap
    this.stopAutoplay();
    var index = parseInt(item.getAttribute('data-index'), 10);
    this.animateToIndex(index);
    this.startAutoplay();
  };

  EonelExplorer.prototype.onDotClick = function (dot) {
    this.stopAutoplay();
    var index = parseInt(dot.getAttribute('data-index'), 10);
    this.animateToIndex(index);
    this.startAutoplay();
  };

  /* -------------------- animation -------------------- */

  EonelExplorer.prototype.scheduleRender = function () {
    if (this.renderScheduled) return;
    this.renderScheduled = true;
    var self = this;
    requestAnimationFrame(function () {
      self.renderScheduled = false;
      self.renderArc(self.state.floatIndex);
    });
  };

  EonelExplorer.prototype.animateToIndex = function (target) {
    target = clamp(target, 0, this.itemCount - 1);
    this.cancelSettle();
    var self = this;

    function step() {
      var s = self.state;
      var diff = target - s.floatIndex;
      if (Math.abs(diff) < SETTLE_EPSILON) {
        s.floatIndex = target;
        self.renderArc(target);
        self.commit(target);
        self.items.forEach(function (item) {
          item.style.willChange = 'auto';
        });
        self.rafId = null;
        return;
      }
      s.floatIndex += diff * SETTLE_EASE;
      self.renderArc(s.floatIndex);
      var nearest = Math.round(s.floatIndex);
      if (nearest !== s.activeIndex) self.commit(nearest);
      self.rafId = requestAnimationFrame(step);
    }
    this.rafId = requestAnimationFrame(step);
  };

  EonelExplorer.prototype.cancelSettle = function () {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  };

  /* -------------------- rendering -------------------- */

  EonelExplorer.prototype.renderArc = function (floatIndex) {
    var angleStepRad = (ANGLE_STEP_DEG * Math.PI) / 180;

    for (var i = 0; i < this.itemCount; i++) {
      var item = this.items[i];
      var offset = i - floatIndex;
      var absOffset = Math.abs(offset);

      if (absOffset > VISIBLE_RANGE) {
        item.style.opacity = '0';
        item.style.pointerEvents = 'none';
        continue;
      }

      var x, y;
      if (this.arcEnabled) {
        var angle = offset * angleStepRad;
        x = Math.sin(angle) * this.radiusX;
        y = (1 - Math.cos(angle)) * this.radiusY;
      } else {
        x = offset * this.spacingPx;
        y = 0;
      }

      var scale = clamp(1 - absOffset * SCALE_STEP, MIN_SCALE, 1);
      var opacity = clamp(1 - absOffset * OPACITY_STEP, MIN_OPACITY, 1);
      var zIndex = Math.round(100 - absOffset * 10);

      item.style.transform =
        'translate3d(' + x.toFixed(2) + 'px,' + y.toFixed(2) + 'px,0) scale(' + scale.toFixed(3) + ')';
      item.style.opacity = String(opacity);
      item.style.zIndex = String(zIndex);
      item.style.pointerEvents = 'auto';
    }
  };

  /* -------------------- state commit + detail card -------------------- */

  EonelExplorer.prototype.commit = function (index, skipTransition) {
    index = clamp(index, 0, this.itemCount - 1);
    this.state.activeIndex = index;

    for (var i = 0; i < this.items.length; i++) {
      this.items[i].setAttribute('aria-selected', i === index ? 'true' : 'false');
    }
    for (var d = 0; d < this.dots.length; d++) {
      var dotIndex = parseInt(this.dots[d].getAttribute('data-index'), 10);
      this.dots[d].setAttribute('aria-selected', dotIndex === index ? 'true' : 'false');
    }

    this.renderDetailCard(index);
    this.preloadNeighbors(index);
  };

  EonelExplorer.prototype.renderDetailCard = function (index) {
    var product = this.data[index];
    if (!product || !this.detail.title) return;

    var img = this.detail.image;
    if (product.image && product.image.src) {
      img.setAttribute('data-loaded', 'false');
      img.src = window.innerWidth < 480 ? product.image.src400 : product.image.src800;
      img.alt = product.image.alt || product.title;
      var markLoaded = function () {
        img.setAttribute('data-loaded', 'true');
      };
      img.onload = markLoaded;
      if (img.complete) markLoaded();
    }

    if (product.badge) {
      this.detail.badge.textContent = product.badge;
      this.detail.badge.hidden = false;
    } else {
      this.detail.badge.hidden = true;
    }

    this.detail.title.textContent = product.title;
    this.detail.desc.textContent = product.description;
    this.detail.price.textContent = product.price;

    if (product.comparePrice) {
      this.detail.compare.textContent = product.comparePrice;
      this.detail.compare.hidden = false;
    } else {
      this.detail.compare.hidden = true;
    }

    if (product.discountPct > 0) {
      this.detail.discount.textContent = product.discountPct + '% OFF';
      this.detail.discount.hidden = false;
    } else {
      this.detail.discount.hidden = true;
    }

    this.detail.cta.href = product.url;
    if (!product.available) {
      this.detail.ctaText.textContent = 'Sold out';
      this.detail.cta.setAttribute('aria-disabled', 'true');
    } else {
      this.detail.ctaText.textContent = this.detail.ctaText.textContent === 'Sold out' ? 'Buy Now' : this.detail.ctaText.textContent;
      this.detail.cta.removeAttribute('aria-disabled');
    }
  };

  /* -------------------- preloading -------------------- */

  EonelExplorer.prototype.preloadNeighbors = function (index) {
    var offsets = [-1, 1, -2, 2];
    for (var o = 0; o < offsets.length; o++) {
      var i = index + offsets[o];
      if (i < 0 || i >= this.data.length) continue;
      var src = this.data[i].image && this.data[i].image.src800;
      if (!src) continue;
      var img = new Image();
      img.src = src;
    }
  };

  /* -------------------- hint -------------------- */

  EonelExplorer.prototype.hideHint = function () {
    if (this.hintEl) this.hintEl.setAttribute('data-hidden', 'true');
  };

  /* -------------------- autoplay -------------------- */

  EonelExplorer.prototype.startAutoplay = function () {
    if (!this.autoplay || this.destroyed) return;
    this.stopAutoplay();
    var self = this;
    this.autoplayTimer = setInterval(function () {
      var next = (self.state.activeIndex + 1) % self.itemCount;
      self.animateToIndex(next);
    }, this.autoplaySpeed);
  };

  EonelExplorer.prototype.stopAutoplay = function () {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  };

  /* -------------------- utils -------------------- */

  EonelExplorer.prototype.debounce = function (fn, wait) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  };

  /* -------------------- teardown -------------------- */

  EonelExplorer.prototype.destroy = function () {
    this.destroyed = true;
    this.stopAutoplay();
    this.cancelSettle();
    this.trackEl.removeEventListener('pointerdown', this._onPointerDown);
    window.removeEventListener('pointermove', this._onPointerMove);
    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerUp);
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('shopify:block:select', this._onBlockSelect);
    document.removeEventListener('shopify:section:unload', this._onSectionUnload);
    this.root.eonelExplorerInstance = null;
    this.root.removeAttribute('data-eonel-initialized');
  };

  /* -------------------- bootstrap -------------------- */

  function initInstance(root) {
    if (!root || root.getAttribute('data-eonel-initialized') === 'true') return;
    root.setAttribute('data-eonel-initialized', 'true');
    var instance = new EonelExplorer(root);
    instance.init();
  }

  function initAll() {
    var roots = document.querySelectorAll('.eonel-explorer');
    for (var i = 0; i < roots.length; i++) initInstance(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme Editor: a new section instance was injected into the DOM.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('.eonel-explorer') : null;
    if (root) initInstance(root);
  });
})();
